// @ts-nocheck


import type { VehicleGroup, VehicleType } from './../../lib/types';
import { vehicleGroupEnum, vehicleTypeEnum } from './../../lib/enum';
import { vehicleTypesPlainTree } from './../../lib/vehicleTypesTree';

const legacyVehicleGroupEnum = vehicleGroupEnum as unknown as Record<
  string,
  VehicleGroup
>;
const legacyVehicleTypeEnum = vehicleTypeEnum as unknown as Record<
  string,
  VehicleType
>;

export const getVehicleGroup = (vehicleType: VehicleType) => {
  let vehicleGroup = '' as VehicleGroup;
  Object.keys(vehicleTypesPlainTree).forEach((key: string) => {
    if (vehicleTypesPlainTree[key]?.includes(vehicleType)) {
      vehicleGroup = key as VehicleGroup;
      return;
    }
  });
  return vehicleGroup;
};

/**
 * Логика по отображению поля "Категория прав для ТС"
 * для заполнения формы
 *
 * ХЗ, зачем эти проверки нужны
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowRightsCategory = (vehicleType: VehicleType) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  if (vehicleGroup === legacyVehicleGroupEnum.bkmBgm) {
    return [
      legacyVehicleTypeEnum.bgmNaTraktore,
      legacyVehicleTypeEnum.bkmNaTraktore,
      legacyVehicleTypeEnum.bkmRpnNaTraktore
    ].includes(vehicleType as never);
  }

  if (vehicleGroup === legacyVehicleGroupEnum.tractorsExcavators) {
    return (
      vehicleTypesPlainTree[legacyVehicleGroupEnum.tractorsExcavators]?.includes(
        vehicleType
      ) ?? false
    );
  }
  return [
    legacyVehicleGroupEnum.cars,
    legacyVehicleGroupEnum.specialBuses,
    legacyVehicleGroupEnum.passengerBuses,
    legacyVehicleGroupEnum.truckCranes,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.tippers,
    legacyVehicleGroupEnum.flatbedTrucks,
    legacyVehicleGroupEnum.cargoVans,
    legacyVehicleGroupEnum.otherTrucks,
    legacyVehicleGroupEnum.utilityVehicles,
    legacyVehicleGroupEnum.autolabs
  ].includes(vehicleGroup as never);
};

/**
 * Логика по отображению поля "Обязательно ОСАГО"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowOsagoNeeded = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  return [
    legacyVehicleGroupEnum.truckCranes,
    legacyVehicleGroupEnum.specialBuses,
    legacyVehicleGroupEnum.passengerBuses,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.bkmBgm,
    legacyVehicleGroupEnum.utilityVehicles,
    legacyVehicleGroupEnum.tippers,
    legacyVehicleGroupEnum.flatbedTrucks,
    legacyVehicleGroupEnum.cargoVans,
    legacyVehicleGroupEnum.otherTrucks,
    legacyVehicleGroupEnum.cars,
    legacyVehicleGroupEnum.tractorsExcavators,
    legacyVehicleGroupEnum.autolabs,

    legacyVehicleGroupEnum.carGroup,
    legacyVehicleGroupEnum.busGroup,
    legacyVehicleGroupEnum.cargoGroup,
    legacyVehicleGroupEnum.specialCarGroup
  ].includes(vehicleGroup as never);
};

/**
 * Логика по отображению поля "Количество осей"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowAxlesNumber = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  return vehicleGroup !== legacyVehicleGroupEnum.floatingTransport;
};

/**
 * Логика по отображению поля "Полная масса ТС, т"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowGrossWeight = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  return vehicleGroup !== legacyVehicleGroupEnum.floatingTransport;
};

/**
 * Логика по отображению поля "Колесная формула"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowChassisConfiguration = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  return (
    vehicleGroup !== legacyVehicleGroupEnum.floatingTransport &&
    vehicleGroup !== legacyVehicleGroupEnum.trailers
  );
};

/**
 * Логика по отображению поля "Нагрузка на ось"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowAxleLoad = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.bkmBgm) {
    return [
      legacyVehicleTypeEnum.bkmNaAvtomobyle,
      legacyVehicleTypeEnum.bkmRpnNaAvtomobyle
    ].includes(vehicleType as never);
  }

  return [
    legacyVehicleGroupEnum.truckCranes,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.tippers,
    legacyVehicleGroupEnum.flatbedTrucks,
    legacyVehicleGroupEnum.cargoVans,
    legacyVehicleGroupEnum.otherTrucks,
    legacyVehicleGroupEnum.utilityVehicles,
    legacyVehicleGroupEnum.autolabs
  ].includes(vehicleGroup as never);
};

/**
 * Логика по отображению поля "Грузоподъемность"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowCarrying = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.bkmBgm) {
    return [
      legacyVehicleTypeEnum.bkmNaAvtomobyle,
      legacyVehicleTypeEnum.bkmRpnNaAvtomobyle
    ].includes(vehicleType as never);
  }

  return [
    legacyVehicleGroupEnum.cars,
    legacyVehicleGroupEnum.specialBuses,
    legacyVehicleGroupEnum.passengerBuses,
    legacyVehicleGroupEnum.truckCranes,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.tippers,
    legacyVehicleGroupEnum.flatbedTrucks,
    legacyVehicleGroupEnum.cargoVans,
    legacyVehicleGroupEnum.otherTrucks,
    legacyVehicleGroupEnum.trailers,
    legacyVehicleGroupEnum.utilityVehicles,
    legacyVehicleGroupEnum.autolabs
  ].includes(vehicleGroup as never);
};

/**
 * Логика по отображению полей, касаемо двигателя
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowEngineData = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  return vehicleGroup !== legacyVehicleGroupEnum.trailers;
};

/**
 * Логика по отображению поля "Кол-во пассажирский мест, шт"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowPassengerSeatsNumber = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.bkmBgm) {
    return [
      legacyVehicleTypeEnum.bkmNaAvtomobyle,
      legacyVehicleTypeEnum.bkmRpnNaAvtomobyle
    ].includes(vehicleType as never);
  }

  if (vehicleGroup === legacyVehicleGroupEnum.atvs) {
    return [legacyVehicleTypeEnum.bolotoxod].includes(vehicleType as never);
  }

  return [
    legacyVehicleGroupEnum.cars,
    legacyVehicleGroupEnum.specialBuses,
    legacyVehicleGroupEnum.passengerBuses,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.tippers,
    legacyVehicleGroupEnum.flatbedTrucks,
    legacyVehicleGroupEnum.cargoVans,
    legacyVehicleGroupEnum.otherTrucks,
    legacyVehicleGroupEnum.utilityVehicles,
    legacyVehicleGroupEnum.autolabs,
    legacyVehicleGroupEnum.floatingTransport
  ].includes(vehicleGroup as never);
};

/**
 * Логика по обязательности заполнения поля "Кол-во пассажирский мест, шт"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const isRequiredPassengerSeatsNumber = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.bkmBgm) {
    return [
      legacyVehicleTypeEnum.bkmNaAvtomobyle,
      legacyVehicleTypeEnum.bkmRpnNaAvtomobyle
    ].includes(vehicleType as never);
  }

  if (vehicleGroup === legacyVehicleGroupEnum.otherTrucks) {
    // Тягачи НЕ(!) должны быть обязательными для заполнения
    return ![
      legacyVehicleTypeEnum.avtomobylTiagachBezKmu,
      legacyVehicleTypeEnum.avtomobylTiagachSKmu
    ].includes(vehicleType as never);
  }

  return [
    legacyVehicleGroupEnum.cars,
    legacyVehicleGroupEnum.specialBuses,
    legacyVehicleGroupEnum.passengerBuses,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.utilityVehicles,
    legacyVehicleGroupEnum.autolabs,
    legacyVehicleGroupEnum.floatingTransport
  ].includes(vehicleGroup as never);
};

/**
 * Логика по отображению поля "Экологический класс ТС"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowEcologicClass = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.bkmBgm) {
    return [
      legacyVehicleTypeEnum.bkmNaAvtomobyle,
      legacyVehicleTypeEnum.bkmRpnNaAvtomobyle
    ].includes(vehicleType as never);
  }

  return [
    legacyVehicleGroupEnum.cars,
    legacyVehicleGroupEnum.specialBuses,
    legacyVehicleGroupEnum.passengerBuses,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.tippers,
    legacyVehicleGroupEnum.flatbedTrucks,
    legacyVehicleGroupEnum.cargoVans,
    legacyVehicleGroupEnum.otherTrucks,
    legacyVehicleGroupEnum.utilityVehicles,
    legacyVehicleGroupEnum.autolabs
  ].includes(vehicleGroup as never);
};

/**
 * Логика по отображению поля "Категория ТС"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowVehicleCategory = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.bkmBgm) {
    return [
      legacyVehicleTypeEnum.bkmNaAvtomobyle,
      legacyVehicleTypeEnum.bkmRpnNaAvtomobyle
    ].includes(vehicleType as never);
  }

  return [
    legacyVehicleGroupEnum.cars,
    legacyVehicleGroupEnum.specialBuses,
    legacyVehicleGroupEnum.passengerBuses,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.tippers,
    legacyVehicleGroupEnum.flatbedTrucks,
    legacyVehicleGroupEnum.cargoVans,
    legacyVehicleGroupEnum.otherTrucks,
    legacyVehicleGroupEnum.trailers,
    legacyVehicleGroupEnum.utilityVehicles,
    legacyVehicleGroupEnum.autolabs,

    legacyVehicleGroupEnum.carGroup,
    legacyVehicleGroupEnum.busGroup,
    legacyVehicleGroupEnum.cargoGroup,
    legacyVehicleGroupEnum.specialCarGroup
  ].includes(vehicleGroup as never);
};

/**
 * Логика по отображению полей для ГПМ
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowGpmData = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.flatbedTrucks) {
    return [
      legacyVehicleTypeEnum.gruzovoiBortovoiN2Do7TnSKmu,
      legacyVehicleTypeEnum.gruzovoiBortovoiN3Svyshe7TnSKmu
    ].includes(vehicleType as never);
  }

  if (vehicleGroup === legacyVehicleGroupEnum.otherTrucks) {
    return vehicleType === legacyVehicleTypeEnum.avtomobylTiagachSKmu;
  }

  if (vehicleGroup === legacyVehicleGroupEnum.utilityVehicles) {
    return vehicleType === legacyVehicleTypeEnum.gruzopassazhyrskyiAvtomobylSKmu;
  }

  if (vehicleGroup === legacyVehicleGroupEnum.trailers) {
    return [legacyVehicleTypeEnum.prytsepSKmu, legacyVehicleTypeEnum.pPrytsepSKmu].includes(
      vehicleType
    );
  }

  return [
    legacyVehicleGroupEnum.truckCranes,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.bkmBgm
  ].includes(vehicleGroup as never);
};

/**
 * Логика по отображению полей для бурильной установки
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowDrillingData = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  return vehicleGroup === legacyVehicleGroupEnum.bkmBgm;
};

/**
 * Логика по отображению полей по топливу
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowFuelData = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  return (
    vehicleGroup !== legacyVehicleGroupEnum.trailers &&
    vehicleType !== legacyVehicleTypeEnum.trailers
  );
};

/**
 * Логика по отображению поля "Дополнительный вид топлива"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowSecondaryFuel = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);
  // (!) Чтобы не прописывать весь список, ставлю отрицание и пишу список запрещенных
  return ![legacyVehicleGroupEnum.atvs, legacyVehicleGroupEnum.floatingTransport].includes(
    vehicleGroup
  );
};

/**
 * Логика по обязательности заполнения полей
 * "Вид топлива"
 * "Норма расх. топлива на 1 маш/час работы"
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const isRequiredAddPrimaryFuelType = (vehicleType?: VehicleType | null) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  return vehicleGroup !== legacyVehicleGroupEnum.floatingTransport;
};

/**
 * Логика по обязательности заполнения поля "Нормативный расход, л/100 км"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const isRequiredAddPrimaryFuelConsumption = (
  vehicleType?: VehicleType | null
) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.bkmBgm) {
    return [
      legacyVehicleTypeEnum.bkmNaAvtomobyle,
      legacyVehicleTypeEnum.bkmRpnNaAvtomobyle
    ].includes(vehicleType as never);
  }
  return ![
    legacyVehicleGroupEnum.atvs,
    legacyVehicleGroupEnum.tractorsExcavators,
    legacyVehicleGroupEnum.floatingTransport
  ].includes(vehicleGroup as never);
};

/**
 * Логика по обязательности заполнения поля "Нормативный расход л/маш.час работы оборудования для основного вида топлива"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const isRequiredAddPrimaryEquipmentFuelConsumption = (
  vehicleType?: VehicleType | null
) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  return ![legacyVehicleGroupEnum.atvs, legacyVehicleGroupEnum.floatingTransport].includes(
    vehicleGroup
  );
};

/**
 * Логика по отображению поля "Нормативный расход л/маш.час работы оборудования для основного вида топлива"
 * для заполнения формы
 *
 * Тип ТС
 * @param {VehicleType} vehicleType
 */
export const canShowPrimaryEquipmentFuelConsumption = (
  vehicleType?: VehicleType | null
) => {
  if (!vehicleType) {
    return false;
  }
  const vehicleGroup = getVehicleGroup(vehicleType);

  if (vehicleGroup === legacyVehicleGroupEnum.trucks) {
    return [
      legacyVehicleTypeEnum.amKlassaN1STsysternoi,
      legacyVehicleTypeEnum.amKlassaN2STsysternoi,
      legacyVehicleTypeEnum.amKlassaN3STsysternoi
    ].includes(vehicleType as never);
  }

  return [
    legacyVehicleGroupEnum.truckCranes,
    legacyVehicleGroupEnum.trucksKmu,
    legacyVehicleGroupEnum.bkmBgm,
    legacyVehicleGroupEnum.carLifts,
    legacyVehicleGroupEnum.autolabs,
    legacyVehicleGroupEnum.tractors
  ].includes(vehicleGroup as never);
};
